import { ImmutableFilterSet } from 'app/overview/dashboard/dashboard_item.service';
import { SelectionType } from 'app/shared/selection/SelectionType';
import Immutable from 'immutable';
import * as _ from 'lodash';

// This service:
//  * Provides functions for modifying filter sets (used to filter collections of assets, ip devices etc.)
export default class FilterService {
    constructor() {
        // noop
    }

    static get FILTER_BY_SITE() {
        return 'site_id';
    }

    static get FILTER_BY_ASSET() {
        return 'asset_id';
    }

    static get FILTER_BY_ASSET_TYPE() {
        return 'asset_type_id';
    }

    static get FILTER_BY_ASSET_TAG() {
        return 'asset_tag_id';
    }

    static get FILTER_BY_IP_DEVICE() {
        return 'ip_device_id';
    }

    static get FILTER_BY_IP_DEVICE_TYPE() {
        return 'ip_device_type_id';
    }

    static get FILTER_BY_IP_DEVICE_TAG() {
        return 'ip_device_tag_id';
    }

    static get FILTER_BY_RADIO() {
        return 'radio_id';
    }

    static get FILTER_BY_RADIO_TECHNOLOGY_CATEGORY() {
        return 'radio_technology_id';
    }

    static get FILTER_BY_RADIO_TECHNOLOGY_TYPE() {
        return 'radio_technology_type';
    }

    // TODO: omitting FILTER_BY_RADIO_TYPE right now because it's a string, not a foreign key id

    static get FILTER_BY_ZONE() {
        return 'zone_id';
    }

    static get FILTER_BY_ZONE_TYPE() {
        return 'zone_type_id';
    }

    static get FILTER_BY_TRIGGER() {
        return 'trigger_id';
    }

    static get FILTER_BY_NAME() {
        return 'name';
    }

    static get FILTER_BY_CONNECTION() {
        return 'connection';
    }

    static get FILTER_BY_SUBGROUP_NAME() {
        return 'subgroup_name';
    }

    static get FILTER_BY_WIFI_CHANNEL() {
        return 'wifi_channel';
    }

    static get emptySet(): ImmutableFilterSet {
        return Immutable.Map();
    }

    static addItemToSet(filterSet: ImmutableFilterSet, type: string, value: number | string) {
        const prev = filterSet.get(type, Immutable.List());
        return prev.includes(value) ? filterSet : filterSet.set(type, prev.push(value));
    }

    static removeItemFromSet(filterSet: ImmutableFilterSet, type: string, value: number | string) {
        if (filterSet.has(type) && filterSet.get(type).includes(value)) {
            const index = filterSet.get(type).indexOf(value);
            const newSet = filterSet.update(type, (type) => type.delete(index));
            return newSet.get(type).size === 0 ? newSet.delete(type) : newSet;
        } else {
            throw `Could not remove value '${value}' of type '${type}' from filterSet`;
        }
    }

    static filterSetFromSelection(selection): ImmutableFilterSet {
        return _.reduce(
            selection,
            (filterSet, selection) => {
                const filterType = Immutable.fromJS({
                    [SelectionType.ASSET]: FilterService.FILTER_BY_ASSET,
                    [SelectionType.IP_DEVICE]: FilterService.FILTER_BY_IP_DEVICE,
                    [SelectionType.RADIO]: FilterService.FILTER_BY_RADIO,
                    [SelectionType.ZONE]: FilterService.FILTER_BY_ZONE,
                    [SelectionType.CONNECTION]: FilterService.FILTER_BY_CONNECTION,
                }).get(selection.type);

                if (!filterType) {
                    return filterSet;
                } else {
                    return FilterService.addItemToSet(filterSet, filterType, selection.id);
                }
            },
            FilterService.emptySet,
        );
    }

    static convertSetTypeToStr(filterSet: ImmutableFilterSet, type: string) {
        const filter = filterSet ? filterSet.get(type, Immutable.List()) : Immutable.List();
        return filter.size > 0 ? filter.join(',') : undefined;
    }

    static convertSetTypeToList(filterSet: ImmutableFilterSet, type: string) {
        return filterSet ? filterSet.get(type, Immutable.List()).toJS() : [];
    }

    static handleFilterOptionDuplicates(
        options,
        nameKey = 'name',
        valueKey = 'value',
    ): Array<{ [key: string]: string }> {
        const lowercaseOptionNames = _.map(options, (option) => _.toLower(_.get(option, nameKey)));
        const duplicates = _.filter(lowercaseOptionNames, (name, index, names) =>
            _.includes(names, name, index + 1),
        );
        return _.map(options, (option) => {
            const lowercaseName = _.toLower(_.get(option, nameKey));
            const isDupe = _.find(duplicates, (name) => name === lowercaseName);
            return Object.assign(
                {},
                option,
                isDupe
                    ? { [nameKey]: `${_.get(option, nameKey)} (${_.get(option, valueKey)})` }
                    : {},
            );
        });
    }
}
